import { types, flow, getRoot } from 'mobx-state-tree';
import api from '../services/api';

const Mindmap = types.model('Mindmap', {
  id: types.identifier,
  name: types.string,
  description: types.maybeNull(types.string),
  organizationId: types.string,
  folderId: types.maybeNull(types.string),
  visibility: types.optional(types.enumeration(['private', 'shared']), 'private'),
  createdBy: types.maybeNull(types.string),
  createdAt: types.string,
  updatedAt: types.string,
  nodeCount: types.optional(types.number, 0),
});

const Folder = types.model('Folder', {
  id: types.identifier,
  name: types.string,
  organizationId: types.string,
  type: types.optional(types.enumeration(['private', 'shared']), 'private'),
  parentFolderId: types.maybeNull(types.string),
  position: types.optional(types.number, 0),
  createdAt: types.string,
  updatedAt: types.string,
});

const OrganizationStore = types
  .model('OrganizationStore', {
    organizationId: types.maybeNull(types.string),
    organizationName: types.maybeNull(types.string),
    mindmaps: types.array(Mindmap),
    folders: types.array(Folder),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .actions(self => ({
    // Safe setters for hydrating from workspace payloads
    setFolders(folders: any[]) {
      const callId = Math.random().toString(36).substr(2, 9);
      console.log(`📁 [OrganizationStore.setFolders] CALL START #${callId} - setting ${folders.length} folders`);
      self.folders.clear();
      folders.forEach((f: any) => self.folders.push(f));
      console.log(`✅ [OrganizationStore.setFolders] #${callId} SUCCESS - ${self.folders.length} folders set`);
    },

    setMindmaps(mindmaps: any[]) {
      const callId = Math.random().toString(36).substr(2, 9);
      console.log(`🧠 [OrganizationStore.setMindmaps] CALL START #${callId} - setting ${mindmaps.length} mindmaps`);
      self.mindmaps.clear();
      mindmaps.forEach((m: any) => self.mindmaps.push(m));
      console.log(`✅ [OrganizationStore.setMindmaps] #${callId} SUCCESS - ${self.mindmaps.length} mindmaps set`);
    },

    setOrganizationId(id: string) {
      console.log(`🏢 [OrganizationStore.setOrganizationId] Setting org ID:`, id);
      self.organizationId = id;
    },
    
    setOrganizationName(name: string) {
      console.log(`🏢 [OrganizationStore.setOrganizationName] Setting org name:`, name);
      self.organizationName = name;
    },
    
    setLoading(loading: boolean) {
      self.isLoading = loading;
    },
    
    setError(error: string | null) {
      self.error = error;
    },
    
    loadFolders: flow(function* () {
      // Guard: Don't run if app isn't initialized
      const appStore = getRoot(self) as any;
      if (!appStore.isInitialized) return;
      
      self.setLoading(true);
      try {
        if (!self.organizationId) throw new Error('No organizationId');
        const response = yield api.get(
          `/folders/organization/${self.organizationId}`,
 // Use httpOnly cookies
        );
        self.folders.clear();
        response.data.forEach((f: any) => self.folders.push({
          id: f.id,
          name: f.name,
          organizationId: f.organizationId,
          type: (f.type as any) || 'private',
          parentFolderId: f.parentFolderId ?? null,
          position: typeof f.position === 'number' ? f.position : 0,
          createdAt: f.createdAt,
          updatedAt: f.updatedAt,
        }));
      } catch (error: any) {
        console.error('[OrganizationStore] Error loading folders:', error);
        self.setError(error.message || 'Failed to load folders');
      } finally {
        self.setLoading(false);
      }
    }),

    // Load hierarchical folder tree (with children) for the organization
    loadFolderTree: flow(function* () {
      try {
        if (!self.organizationId) throw new Error('No organizationId');
        const response = yield api.get(
          `/folders/tree/${self.organizationId}`,
 // Use httpOnly cookies
        );
        return response.data;
      } catch (error) {
        console.error('[OrganizationStore] Error loading folder tree:', error);
        throw error;
      }
    }),
    
    loadMindmaps: flow(function* () {
      // Guard: Don't run if app isn't initialized
      const appStore = getRoot(self) as any;
      if (!appStore.isInitialized) {
        console.log('[OrganizationStore] loadMindmaps: App not initialized, skipping');
        return;
      }
      
      // Guard: Don't run if already loading (prevents spam)
      if (self.isLoading) {
        console.log('[OrganizationStore] loadMindmaps: Already loading, skipping duplicate call');
        return;
      }
      
      console.log('[OrganizationStore] loadMindmaps called - starting...');
      self.setLoading(true);
      // Clear any previous errors when loading
      self.setError(null);
      
      try {
        // Token is now in httpOnly cookies, no need to check localStorage
        console.log('[OrganizationStore] Using httpOnly cookies for authentication');
        
        console.log('[OrganizationStore] Fetching mindmaps from API...');
        const response = yield api.get('/mindmaps', {
          withCredentials: true // Use httpOnly cookies
        });
        
        console.log('[OrganizationStore] API Response:', response.data);
        console.log('[OrganizationStore] Loaded mindmaps count:', response.data?.length || 0);
        console.log('[OrganizationStore] Mindmap list:', response.data?.map((m: any) => ({
          id: m.id,
          name: m.name,
          organizationId: m.organizationId,
          folderId: m.folderId,
          visibility: m.visibility
        })));
        
        // Clear existing mindmaps
        self.mindmaps.clear();
        
        // Load mindmap details in parallel
        const mindmapDetails = yield Promise.all(
          response.data.map((mindmap: any) => 
            api.get(`/mindmaps/${mindmap.id}`, {
              withCredentials: true // Use httpOnly cookies
            }).then(res => {
              const nodeCount = res.data.nodes?.length || 0;
              console.log(`[OrganizationStore] Loaded nodes for mindmap "${mindmap.name}" (${mindmap.id}):`, nodeCount);
              return {
                ...mindmap,
                nodeCount
              };
            }).catch((error) => {
              console.error(`[OrganizationStore] Failed to load nodes for mindmap "${mindmap.name}" (${mindmap.id}):`, error);
              return {
                ...mindmap,
                nodeCount: 0
              };
            })
          )
        );
        
        // Add mindmaps to store
        mindmapDetails.forEach((mindmap: any) => {
          // Check if mindmap already exists to prevent duplicates
          const exists = self.mindmaps.some(m => m.id === mindmap.id);
          if (!exists) {
            const mindmapToAdd = {
              id: mindmap.id,
              name: mindmap.name,
              description: mindmap.description || null,
              organizationId: mindmap.organizationId,
              folderId: mindmap.folderId || null,
              visibility: (mindmap.visibility as any) || 'private',
              createdBy: mindmap.createdBy || null,
              createdAt: mindmap.createdAt || new Date().toISOString(),
              updatedAt: mindmap.updatedAt || new Date().toISOString(),
              nodeCount: mindmap.nodeCount || 0,
            };
            console.log('[OrganizationStore] Adding mindmap to store:', mindmapToAdd.name, 'nodeCount:', mindmapToAdd.nodeCount);
            self.mindmaps.push(mindmapToAdd);
          }
        });
        
        console.log('[OrganizationStore] Final mindmaps count:', self.mindmaps.length);
        console.log('[OrganizationStore] Mindmaps with node counts:', self.mindmaps.map(m => ({
          id: m.id,
          name: m.name,
          nodeCount: m.nodeCount
        })));
        
      } catch (error: any) {
        console.error('[OrganizationStore] Error loading mindmaps:', error);
        self.setError(error.message || 'Failed to load mindmaps');
      } finally {
        self.setLoading(false);
      }
    }),

    moveMindmapToFolder: flow(function* (mindmapId: string, folderId: string | null) {
      try {
        yield api.patch(
          `/mindmaps/${mindmapId}/move`,
          { folderId },
          { toastMessage: 'Mindmap moved to folder' }
        );
        const m = self.mindmaps.find(mm => mm.id === mindmapId);
        if (m) {
          (m as any).folderId = folderId as any;
        }
      } catch (error) {
        console.error('[OrganizationStore] Error moving mindmap:', error);
      }
    }),
    
    setMindmapVisibility: flow(function* (mindmapId: string, visibility: 'private' | 'shared') {
      try {
        yield api.patch(
          `/mindmaps/${mindmapId}/visibility`,
          { visibility },
          { toastMessage: `Mindmap set to ${visibility}` }
        );
        const m = self.mindmaps.find(mm => mm.id === mindmapId);
        if (m) {
          (m as any).visibility = visibility as any;
        }
      } catch (error) {
        console.error('[OrganizationStore] Error updating visibility:', error);
      }
    }),
    
    createMindmap: flow(function* (name: string, description?: string, folderId?: string, visibility: 'private' | 'shared' = 'private') {
      try {
        // Token is now in httpOnly cookies
        
        const payload = { 
          name, 
          description, 
          folderId: folderId || null,
          visibility,
          organizationId: self.organizationId 
        };
        
        console.log('[OrganizationStore] Creating mindmap with payload:', payload);
        
        const response = yield api.post(
          '/mindmaps',
          payload,
          { toastMessage: 'Mindmap created' }
        );
        
        console.log('[OrganizationStore] Created mindmap response:', response.data);
        
        // Add to store with all required fields
        const newMindmap = {
          id: response.data.id,
          name: response.data.name || name,
          description: response.data.description || description || null,
          organizationId: response.data.organizationId || self.organizationId,
          folderId: response.data.folderId || folderId || null,
          visibility: response.data.visibility || visibility,
          createdBy: response.data.createdBy || null,
          createdAt: response.data.createdAt || new Date().toISOString(),
          updatedAt: response.data.updatedAt || new Date().toISOString(),
          nodeCount: response.data.nodeCount || 0
        };
        
        console.log('[OrganizationStore] Adding to store:', newMindmap);
        self.mindmaps.push(newMindmap);
        
        // Return the new mindmap - the component will handle navigation
        return newMindmap;
      } catch (error: any) {
        console.error('[OrganizationStore] Error creating mindmap:', error);
        self.setError(error.message || 'Failed to create mindmap');
        throw error;
      }
    }),
    
    deleteMindmap: flow(function* (mindmapId: string) {
      try {
        // Token is now in httpOnly cookies
        
        yield api.delete(`/mindmaps/${mindmapId}`, {
          toastMessage: 'Mindmap deleted'
        });
        
        console.log('[OrganizationStore] Deleted mindmap:', mindmapId);
        
        // Remove from store
        const index = self.mindmaps.findIndex(m => m.id === mindmapId);
        if (index >= 0) {
          self.mindmaps.splice(index, 1);
        }
        
      } catch (error: any) {
        console.error('[OrganizationStore] Error deleting mindmap:', error);
        self.setError(error.message || 'Failed to delete mindmap');
        throw error;
      }
    }),
    
    renameMindmap: flow(function* (mindmapId: string, newName: string) {
      try {
        // Token is now in httpOnly cookies
        
        yield api.patch(
          `/mindmaps/${mindmapId}`,
          { name: newName },
          { toastMessage: 'Mindmap renamed' }
        );
        
        const mindmap = self.mindmaps.find(m => m.id === mindmapId);
        if (mindmap) {
          mindmap.name = newName;
        }
      } catch (error: any) {
        console.error('[OrganizationStore] Error renaming mindmap:', error);
        throw error;
      }
    }),
    
    duplicateMindmap: flow(function* (mindmapId: string) {
      try {
        // Token is now in httpOnly cookies
        
        const response = yield api.post(
          `/mindmaps/${mindmapId}/duplicate`,
          {},
          { toastMessage: 'Mindmap duplicated' }
        );
        
        self.mindmaps.push(response.data);
        return response.data;
      } catch (error: any) {
        console.error('[OrganizationStore] Error duplicating mindmap:', error);
        throw error;
      }
    }),
    
    createFolder: flow(function* (name: string, type: 'private' | 'shared' = 'private', parentFolderId?: string | null) {
      try {
        // Token is now in httpOnly cookies
        
        const response = yield api.post(
          '/folders',
          { name, organizationId: self.organizationId, type, parentFolderId: parentFolderId ?? null },
          { toastMessage: 'Folder created' }
        );
        
        self.folders.push({
          id: response.data.id,
          name: response.data.name,
          organizationId: response.data.organizationId,
          type: (response.data.type as any) || 'private',
          parentFolderId: response.data.parentFolderId ?? null,
          position: typeof response.data.position === 'number' ? response.data.position : 0,
          createdAt: response.data.createdAt,
          updatedAt: response.data.updatedAt,
        });
        return response.data;
      } catch (error: any) {
        console.error('[OrganizationStore] Error creating folder:', error);
        throw error;
      }
    }),
    
    renameFolder: flow(function* (folderId: string, newName: string) {
      try {
        // Token is now in httpOnly cookies
        
        yield api.patch(
          `/folders/${folderId}`,
          { name: newName },
          { toastMessage: 'Folder renamed' }
        );
        
        const folder = self.folders.find(f => f.id === folderId);
        if (folder) {
          folder.name = newName;
        }
      } catch (error: any) {
        console.error('[OrganizationStore] Error renaming folder:', error);
        throw error;
      }
    }),

    updateFolder: flow(function* (folderId: string, updates: { name?: string; type?: 'private' | 'shared' }) {
      try {
        // Token is now in httpOnly cookies
        
        yield api.patch(
          `/folders/${folderId}`,
          updates,
          { toastMessage: 'Folder updated' }
        );
        
        const folder = self.folders.find(f => f.id === folderId);
        if (folder) {
          if (updates.name) folder.name = updates.name;
          if (updates.type) folder.type = updates.type;
        }
      } catch (error) {
        console.error('[OrganizationStore] Failed to update folder:', error);
        throw error;
      }
    }),
    
    deleteFolder: flow(function* (folderId: string) {
      try {
        // Token is now in httpOnly cookies
        
        yield api.delete(`/folders/${folderId}`, {
          toastMessage: 'Folder deleted'
        });
        
        const index = self.folders.findIndex(f => f.id === folderId);
        if (index >= 0) {
          self.folders.splice(index, 1);
        }
        
        // Move mindmaps out of deleted folder
        self.mindmaps.forEach(m => {
          if (m.folderId === folderId) {
            m.folderId = null;
          }
        });
      } catch (error: any) {
        console.error('[OrganizationStore] Error deleting folder:', error);
        throw error;
      }
    }),

    moveFolder: flow(function* (folderId: string, newParentId: string | null) {
      try {
        // Token is now in httpOnly cookies
        yield api.post(
          `/folders/${folderId}/move`,
          { newParentId },
          { toastMessage: 'Folder moved' }
        );
        const folder = self.folders.find(f => f.id === folderId);
        if (folder) {
          (folder as any).parentFolderId = newParentId as any;
        }
      } catch (error) {
        console.error('[OrganizationStore] Error moving folder:', error);
        throw error;
      }
    }),

    reorderFolders: flow(function* (parentFolderId: string | null, folderOrder: string[]) {
      try {
        // Token is now in httpOnly cookies
        yield api.post(
          '/folders/reorder',
          { parentFolderId, folderOrder },
        );
        // Optimistically update positions
        folderOrder.forEach((id, idx) => {
          const f = self.folders.find(ff => ff.id === id);
          if (f) (f as any).position = idx;
        });
      } catch (error) {
        console.error('[OrganizationStore] Error reordering folders:', error);
        throw error;
      }
    }),
    
    // Reset store to initial state
    reset() {
      console.log('🔄 [OrganizationStore] Resetting...');
      self.organizationId = null;
      self.organizationName = null;
      self.mindmaps.clear();
      self.folders.clear();
      self.isLoading = false;
      self.error = null;
    },
  }))
  .views(self => ({
    get mindmapById() {
      return (id: string) => self.mindmaps.find(m => m.id === id);
    },
    
    get sortedMindmaps() {
      return self.mindmaps.slice().sort((a, b) => 
        new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      );
    },
    
    getRootMindmaps() {
      return self.mindmaps.filter(m => !m.folderId);
    },
    
    getMindmapsInFolder(folderId: string) {
      return self.mindmaps.filter(m => m.folderId === folderId);
    },
  }))
  .actions(self => ({
    // WebSocket patch handlers for real-time mindmap updates
    addMindmapFromWebSocket(data: any) {
      console.log(`📥 [OrganizationStore] Adding mindmap from WebSocket:`, data.id);
      const exists = self.mindmaps.some(m => m.id === data.id);
      if (exists) {
        console.log(`⏭️  [OrganizationStore] Mindmap already exists, skipping`);
        return;
      }
      
      self.mindmaps.push({
        id: data.id,
        name: data.name,
        description: data.description || null,
        organizationId: data.organizationId || data.organization_id,
        folderId: data.folderId || data.folder_id || null,
        visibility: data.visibility || 'private',
        createdBy: data.createdBy || data.created_by || null,
        createdAt: data.createdAt || data.created_at || new Date().toISOString(),
        updatedAt: data.updatedAt || data.updated_at || new Date().toISOString(),
        nodeCount: data.nodeCount || data.node_count || 0,
      });
    },
    
    updateMindmapFromWebSocket(data: any) {
      console.log(`📝 [OrganizationStore] Updating mindmap from WebSocket:`, data.id);
      const mindmap = self.mindmaps.find(m => m.id === data.id);
      if (!mindmap) {
        console.warn(`⚠️  [OrganizationStore] Mindmap not found: ${data.id}`);
        return;
      }
      
      // Apply updates
      if (data.name) mindmap.name = data.name;
      if (data.description !== undefined) mindmap.description = data.description;
      if (data.visibility) mindmap.visibility = data.visibility;
      if (data.folderId !== undefined) mindmap.folderId = data.folderId;
    },
    
    removeMindmapFromWebSocket(mindmapId: string) {
      console.log(`🗑️  [OrganizationStore] Removing mindmap from WebSocket:`, mindmapId);
      const index = self.mindmaps.findIndex(m => m.id === mindmapId);
      if (index >= 0) {
        self.mindmaps.splice(index, 1);
      }
    },
  }));

export { OrganizationStore };
export type IOrganizationStore = typeof OrganizationStore.Type;
